import { useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import type { IWizardLegendItems } from "@similarweb/ui-components";
import { EWizardLegendItemState } from "@similarweb/ui-components";

export const PLAYBOOK_WIZARD_STEPS = ["score_criteria", "employee_profiles", "connect_cadence", "summary"] as const;

export type PlaybookWizardStep = (typeof PLAYBOOK_WIZARD_STEPS)[number];

export const PLAYBOOK_STEP_INDEX = {
    scoreCriteria: PLAYBOOK_WIZARD_STEPS.indexOf("score_criteria"),
    employeeProfiles: PLAYBOOK_WIZARD_STEPS.indexOf("employee_profiles"),
    cadence: PLAYBOOK_WIZARD_STEPS.indexOf("connect_cadence"),
    summary: PLAYBOOK_WIZARD_STEPS.indexOf("summary"),
} as const;

const LAST_STEP_INDEX = PLAYBOOK_WIZARD_STEPS.length - 1;

const STEP_REQUIRED_TOOLTIP_KEYS: Partial<Record<number, string>> = {
    [PLAYBOOK_STEP_INDEX.scoreCriteria]: "si.playbooks.wizard.score_criteria.required_tooltip",
    [PLAYBOOK_STEP_INDEX.cadence]: "si.playbooks.wizard.cadence.required_tooltip",
    [PLAYBOOK_STEP_INDEX.summary]: "si.playbooks.wizard.summary.required_tooltip",
};

type UsePlaybookWizardParams = {
    name: string;
    selectedCadenceId: number | null;
    isScoringRuleValid: boolean;
    initialStep?: number;
};

export const usePlaybookWizard = ({
    name,
    selectedCadenceId,
    isScoringRuleValid,
    initialStep = 0,
}: UsePlaybookWizardParams) => {
    const { translate } = useTranslate();
    const [activeStep, setActiveStep] = useState(initialStep);

    const isStepValid = (index: number): boolean => {
        if (index === PLAYBOOK_STEP_INDEX.scoreCriteria) {
            return isScoringRuleValid;
        }
        if (index === PLAYBOOK_STEP_INDEX.cadence) {
            return selectedCadenceId !== null;
        }
        if (index === PLAYBOOK_STEP_INDEX.summary) {
            return name.trim().length > 0;
        }
        return true;
    };

    const arePreviousStepsValid = (targetIndex: number): boolean => {
        for (let index = 0; index < targetIndex; index += 1) {
            if (!isStepValid(index)) {
                return false;
            }
        }
        return true;
    };

    const moveToStep = (targetIndex: number): boolean => {
        if (targetIndex === activeStep) {
            return false;
        }
        if (targetIndex < activeStep || arePreviousStepsValid(targetIndex)) {
            setActiveStep(targetIndex);
            return true;
        }
        return false;
    };

    const goNext = (): void => {
        if (activeStep < LAST_STEP_INDEX && isStepValid(activeStep)) {
            setActiveStep(activeStep + 1);
        }
    };

    const goBack = (): void => {
        if (activeStep > 0) {
            setActiveStep(activeStep - 1);
        }
    };

    const legendItems: IWizardLegendItems = PLAYBOOK_WIZARD_STEPS.map((step, index) => {
        let itemState: EWizardLegendItemState;
        if (index === activeStep) {
            itemState = EWizardLegendItemState.ACTIVE;
        } else if (index < activeStep) {
            itemState = isStepValid(index) ? EWizardLegendItemState.COMPLETED : EWizardLegendItemState.NOT_COMPLETED;
        } else {
            itemState = EWizardLegendItemState.NOT_VISITED;
        }

        return {
            title: translate(`si.playbooks.wizard.step.${step}.title`),
            itemState,
            isStepCompleted: isStepValid(index),
            clickableItemTooltip: translate(`si.playbooks.wizard.step.${step}.title`),
            disabledItemTooltip: translate("si.playbooks.wizard.step.locked_tooltip"),
        };
    });

    const isCurrentStepValid = isStepValid(activeStep);

    const requiredTooltipKey = STEP_REQUIRED_TOOLTIP_KEYS[activeStep];
    const primaryDisabledTooltip =
        !isCurrentStepValid && requiredTooltipKey ? translate(requiredTooltipKey) : undefined;

    return {
        activeStep,
        isFirstStep: activeStep === 0,
        isLastStep: activeStep === LAST_STEP_INDEX,
        isCadenceStepActive: activeStep === PLAYBOOK_STEP_INDEX.cadence,
        isCurrentStepValid,
        primaryDisabledTooltip,
        legendItems,
        goNext,
        goBack,
        moveToStep,
    };
};

export type PlaybookWizardNavigation = ReturnType<typeof usePlaybookWizard>;
